import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  IconButton,
  Typography,
  Grid,
  Paper,
  Button,
  Box
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import LoadingButton from '../components/LoadingButton'
import LoadingCircle from '../components/common/LoadingCircle'
import { smallScreenWidth } from '../constants/AppConstants'
import OpenSearchDropdown from '../components/OpenSearchDropdown'
import { AntTabs } from '../components/VendorTabs'
import { debounce } from 'lodash'
import ResourceMain from '../components/ResourceMain'
import fetchFromAthena from '../functions/FetchFromAthena'
import { DashboardAthenaTable } from '../components/DashboardAthenaTable'
import { resourceCompensationCols, resourcePerformanceHistoryCols, resourceCommentsCols, resourceCustomFieldsCols } from '../constants/ResourceCols'
import ResourceTalentProfile from '../components/ResourceTalentProfile'
import ResourceWorkAssignments from '../components/ResourceWorkAssignments'
import ResourceEmployeeRelations from '../components/ResourceEmployeeRelations'
import DocumentList from '../components/DocumentList'

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    margin: '2rem 1rem'
  }
}))

const defaultHeaders = {
  hrOrg: '',
  employee: ''
}

function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`resource-tabpanel-${index}`}
      aria-labelledby={`resource-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          {children}
        </Box>
      )}
    </div>
  )
}

export default function Resource(props) {
  const [state, setState] = useState({
    fetched: true,
    isLoading: false
  })
  const [showTable, setShowTable] = useState(false)
  const [headers, setHeaders] = useState(defaultHeaders)
  const [tabValue, setTabValue] = useState(0)
  const [tableRefreshKey, setTableRefreshKey] = useState(0)
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const [resourceData, setResourceData] = useState({})
  const [isLoadingMainData, setIsLoadingMainData] = useState(false)
  const [hrOrg, setHrOrg] = useState('')
  const [employee, setEmployee] = useState('')
  const [clearTrigger, setClearTrigger] = useState(false)
  const classes = useStyles()

  const { credentials } = props.fetchInitialData

  const debouncedSubmit = React.useCallback(
    debounce(() => {
      setTableRefreshKey(prevKey => prevKey + 1)

      setTimeout(() => {
        setIsSubmitDisabled(false)
      }, 2000)
    }, 300),
    [headers]
  )

  const handleSubmit = e => {
    e.preventDefault()
    if (!isSubmitDisabled) {
      setIsSubmitDisabled(true)
      setShowTable(false)
      setTabValue(0)
      setState(prevState => ({ ...prevState, isLoading: true }))
      debouncedSubmit()
    }
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const fetchMainData = async () => {
    try {
      setIsLoadingMainData(true)

      const data = await Promise.allSettled(
        ['hr_resource_main'].map(query =>
          fetchFromAthena({
            queryName: query,
            headers,
            accessToken: credentials.user.accessToken,
            credentials
          })
        )
      ).then(results => results)
      setResourceData(data?.[0]?.value?.[0] || {})
      setShowTable(true)
      setIsLoadingMainData(false)
      setState(prevState => ({ ...prevState, isLoading: false }))
    } catch (error) {
      console.error('Error fetching resource main data:', error)
      setResourceData({})
      setIsLoadingMainData(false)
      setState(prevState => ({ ...prevState, isLoading: false }))
    }
  }

  useEffect(() => {
    if (tableRefreshKey > 0) {
      fetchMainData()
    }
  }, [tableRefreshKey])

  if (!state.fetched) {
    return <LoadingCircle />
  }

  return (
    <div style={{ padding: '1.5rem 0.5rem' }}>
      <IconButton
        style={{ borderRadius: '5px', position: 'absolute' }}
        size='small'
        onClick={() => props.history.push('/')}
        aria-label='back'
      >
        <ArrowBackIcon />
        {props.fetchInitialData.credentials.appWidth > smallScreenWidth && 'Dashboard'}
      </IconButton>

      <Typography variant='h5' style={{ textAlign: 'center' }}>
        Resource
      </Typography>

      <div style={{ margin: '1rem' }}>
        <Paper style={{ padding: '1rem' }}>
          <form
            onSubmit={e => {
              e.preventDefault()
              handleSubmit(e)
            }}
          >
            <Grid
              container
              spacing={2}
              direction='row'
              justifyContent='flex-start'
              alignItems='center'
            >
              <Grid item>
                <OpenSearchDropdown
                  id='hrOrg'
                  required
                  width='250px'
                  label='HR Organization'
                  page='RESOURCE'
                  entity='HRORG'
                  searchFields={['DISPLAY']}
                  accessToken={credentials.user.accessToken}
                  credentials={credentials}
                  clearTrigger={clearTrigger}
                  onChange={val => {
                    const hrOrgValue = val?.VALUE || null
                    setHrOrg(hrOrgValue)
                    setHeaders({ ...headers, hrOrg: hrOrgValue })
                  }}
                />
              </Grid>
              <Grid item>
                <OpenSearchDropdown
                  id='employee'
                  required
                  disabled={!hrOrg}
                  width='300px'
                  label='Resource'
                  page='RESOURCE'
                  entity='EMPLOYEE'
                  searchFields={['DISPLAY']}
                  accessToken={credentials.user.accessToken}
                  credentials={credentials}
                  clearTrigger={clearTrigger}
                  // filters={{ HRORG: hrOrg }}
                  onChange={val => {
                    const employeeValue = val?.VALUE || null
                    setEmployee(employeeValue)
                    setHeaders({ ...headers, employee: employeeValue })
                  }}
                />
              </Grid>
            </Grid>

            <div style={{ marginTop: '1rem' }}>
              <LoadingButton
                label='Submit'
                isLoading={state.isLoading}
                color='primaryVLButton'
                buttonType='submit'
                disabled={isSubmitDisabled}
              />
              {!!(hrOrg || employee) && (
                <Button
                  onClick={() => {
                    setHeaders(defaultHeaders)
                    setHrOrg('')
                    setEmployee('')
                    setClearTrigger(prev => !prev)
                  }}
                  style={{ marginLeft: '0.5rem' }}
                >
                  Clear
                </Button>
              )}
            </div>
          </form>
        </Paper>

        {showTable && (
          <div style={{ marginTop: '1rem' }}>
            <Paper>
              <div style={{ padding: '1rem 1rem 0 1rem' }}>
                <Typography variant='h5'>{resourceData.PRESENTATIONNAMESNAPSHOT}</Typography>
                <Typography variant='subtitle2'>Resource ID: {resourceData.EMPLOYEE}</Typography>
                <DocumentList
                  path={`employees/${resourceData.HRORGANIZATION}/${resourceData.EMPLOYEE}`}
                  credentials={credentials}
                  linkText="View Documents"
                  title="Resource Documents"
                  baseFolderLabel="Documents"
                  key={`${resourceData.HRORGANIZATION}_${resourceData.EMPLOYEE}`}
              />
              </div>
              <div style={{ padding: '0 1rem' }}>
                <AntTabs
                  value={tabValue}
                  handleChange={handleTabChange}
                  color='#1976d2'
                  tabs={[
                    'Main',
                    'Work Assignments',
                    'Compensation',
                    'Talent Profile',
                    'Performance History',
                    'Employee Relations',
                    'Resource Comments',
                    'Custom Fields'
                  ]}
                />
              </div>

              <TabPanel value={tabValue} index={0}>
                <ResourceMain data={resourceData} isLoading={isLoadingMainData} />
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <ResourceWorkAssignments
                  {...props}
                  headers={headers}
                  tableRefreshKey={tableRefreshKey}
                />
              </TabPanel>
              <TabPanel value={tabValue} index={2}>
                <DashboardAthenaTable
                  {...props}
                  isTempTable={false}
                  key={`compensation-${tableRefreshKey}`}
                  params={{ ...headers }}
                  tableColumns={resourceCompensationCols}
                  reportName='hr_resource_compensation'
                />
              </TabPanel>
              <TabPanel value={tabValue} index={3}>
                <ResourceTalentProfile
                  {...props}
                  headers={headers}
                  tableRefreshKey={tableRefreshKey}
                />
              </TabPanel>
              <TabPanel value={tabValue} index={4}>
                <DashboardAthenaTable
                  {...props}
                  isTempTable={false}
                  key={`performance-history-${tableRefreshKey}`}
                  params={{ ...headers }}
                  tableColumns={resourcePerformanceHistoryCols}
                  reportName='hr_resource_performance_history'
                />
              </TabPanel>
              <TabPanel value={tabValue} index={5}>
                <ResourceEmployeeRelations
                  {...props}
                  headers={headers}
                  tableRefreshKey={tableRefreshKey}
                />
              </TabPanel>
              <TabPanel value={tabValue} index={6}>
                <DashboardAthenaTable
                  {...props}
                  isTempTable={false}
                  key={`comments-${tableRefreshKey}`}
                  params={{ ...headers }}
                  tableColumns={resourceCommentsCols}
                  reportName='hr_resource_comments'
                />
              </TabPanel>
              <TabPanel value={tabValue} index={7}>
                <DashboardAthenaTable
                  {...props}
                  isTempTable={false}
                  key={`custom-fields-${tableRefreshKey}`}
                  params={{ ...headers }}
                  tableColumns={resourceCustomFieldsCols}
                  reportName='hr_resource_custom_fields'
                />
              </TabPanel>
            </Paper>
          </div>
        )}
      </div>
    </div>
  )
}
